import React, { useState, useEffect, useRef } from 'react';
import { Box, Chip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Tooltip } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import CustomButton from '../buttons/CustomButton';
import AreaTitle from '../areaTitle/AreaTitle';
import styles from './ExportHistory.module.css';
import { DataProviderInstance } from '../../api/DataProvider';
import { formatBytes } from './exportFormat';
import { getCurrentTask, getExpiresAt, getIncludeAttachments, getTotalBytes } from './exportJob';
import ExportDownloadButtons from './ExportDownloadButtons';
import { useRoleAccess } from '../../hooks/useRoleAccess';

const POLL_INTERVAL = 2000; // 2 seconds

/**
 * Histórico de exportações completas. A lista é recarregada ao montar, quando
 * `refreshKey` muda (exportação criada pelo card ou pelo diálogo "Encerrar
 * conta"), ao cancelar e quando um job acompanhado termina. Não há recarga
 * periódica: cada job em andamento é acompanhado pelo status.php a cada 2 s.
 * `onHistoryUpdate` repassa a lista para quem precisa dela (card e página);
 * `onJobFinished(job)` avisa quando um job acompanhado termina (a página
 * baixa sozinha o que foi criado nesta tela).
 */
const ExportHistory = ({ companyId, onHistoryUpdate, onJobFinished, refreshKey = 0 }) => {
    const { GetExportHistory, GetExportStatus, CancelExport } = DataProviderInstance();
    const { canAccess } = useRoleAccess();
    const [history, setHistory] = useState([]);
    const pollsRef = useRef(new Map());

    // Só a pessoa responsável pela conta baixa e cancela exportações (ACCESS_AREAS.dataExport)
    const canManageExports = canAccess('dataExport');

    useEffect(() => {
        const polls = pollsRef.current;
        loadHistory();
        return () => {
            // Clear all polling intervals
            polls.forEach((h) => clearInterval(h));
            polls.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, refreshKey]);

    // Update parent component when history changes
    useEffect(() => {
        onHistoryUpdate?.(history);
    }, [history, onHistoryUpdate]);

    const loadHistory = () => {
        GetExportHistory((data) => {
            if (data.error) {

                return;
            }
            setHistory(data.history || []);

            // Start polling for any pending/processing exports
            data.history?.forEach(item => {
                if (['pending', 'processing'].includes(item.status)) {
                    startPolling(item.id);
                }
            });
        });
    };

    const startPolling = (exportId) => {
        if (pollsRef.current.has(exportId)) return;

        const interval = setInterval(() => {
            GetExportStatus(exportId, (data) => {
                if (data.error) {
                    clearInterval(interval);
                    pollsRef.current.delete(exportId);
                    return;
                }

                setHistory(prev => prev.map(item =>
                    item.id == exportId ? { ...item, ...data } : item
                ));

                if (['complete', 'error', 'cancelled'].includes(data.status)) {
                    clearInterval(interval);
                    pollsRef.current.delete(exportId);
                    onJobFinished?.({ id: exportId, ...data });
                    loadHistory(); // Refresh to get final state
                }
            });
        }, POLL_INTERVAL);

        pollsRef.current.set(exportId, interval);
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'pending':
                return 'Aguardando';
            case 'processing':
                return 'Processando';
            case 'complete':
                return 'Concluído';
            case 'error':
                return 'Erro';
            case 'expired':
                return 'Expirado';
            default:
                return status;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const handleCancel = (exportId) => {
        CancelExport(exportId, (result) => {
            if (!result.error) {
                // Update history after cancellation
                loadHistory();
            }
        });
    };

    const renderProgress = (item) => {
        const currentTask = getCurrentTask(item);

        if (['pending', 'processing'].includes(item.status)) {
            return (
                <div className={styles.progressCell}>
                    <CircularProgress
                        variant="determinate"
                        value={item.progress || 0}
                        size={24}
                    />
                    <Typography variant="body2" className={styles.progressText}>
                        {item.progress}%
                    </Typography>
                    {currentTask && (
                        <Typography variant="caption" color="textSecondary" className={styles.taskText}>
                            {currentTask}
                        </Typography>
                    )}
                </div>
            );
        }
        // Job com erro: current_task traz o motivo (ex.: bucket não configurado).
        if (item.status === 'error' && currentTask) {
            return (
                <Typography variant="caption" color="error" className={styles.taskText}>
                    {currentTask}
                </Typography>
            );
        }
        return `${item.progress}%`;
    };

    // Helper function to render status badge
    const renderStatusBadge = (status) => {
        let statusClass;
        switch (status) {
            case 'pending':
                statusClass = styles.statusPending;
                break;
            case 'processing':
                statusClass = styles.statusProcessing;
                break;
            case 'complete':
                statusClass = styles.statusComplete;
                break;
            case 'error':
                statusClass = styles.statusError;
                break;
            case 'expired':
                statusClass = styles.statusExpired;
                break;
            default:
                statusClass = styles.statusPending;
        }

        return <span className={statusClass}>{getStatusText(status)}</span>;
    };

    return (
        <div className={styles.container}>
            <AreaTitle
                Title="Histórico"
                Description="Veja o histórico de exportações de dados da clínica."
                Icon={<HistoryOutlinedIcon />}
            />
            <TableContainer component={Paper} className={styles.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Progresso</TableCell>
                            <TableCell>Tamanho</TableCell>
                            <TableCell>Concluído em</TableCell>
                            <TableCell>Expira em</TableCell>
                            <TableCell>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {history.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {formatDate(item.created_at)}
                                        {getIncludeAttachments(item) && (
                                            <Chip label="com anexos" size="small" variant="outlined" />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>{renderStatusBadge(item.status)}</TableCell>
                                <TableCell>{renderProgress(item)}</TableCell>
                                <TableCell>
                                    {getTotalBytes(item) ? formatBytes(getTotalBytes(item)) : '—'}
                                </TableCell>
                                <TableCell>
                                    {item.completed_at ? formatDate(item.completed_at) : '-'}
                                </TableCell>
                                <TableCell>
                                    {item.expires_at ? formatDate(item.expires_at) : '-'}
                                </TableCell>
                                <TableCell>
                                    <div className={styles.actionsCell}>
                                        {item.status == 'complete' && (item.download_url || (item.parts && item.parts.length > 0)) && (
                                            <>
                                                {new Date(getExpiresAt(item)) > new Date() ? (
                                                    <ExportDownloadButtons item={item} canManage={canManageExports} />
                                                ) : (
                                                    <Typography variant="caption" color="error">
                                                        Download expirado
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                        {['pending', 'processing'].includes(item.status) && (
                                            <Tooltip
                                                title={!canManageExports ? "Só a pessoa responsável pela conta pode cancelar exportações" : "Cancelar exportação"}
                                                arrow
                                            >
                                                <span>
                                                    <CustomButton
                                                        style="secondary"
                                                        icon={<CancelOutlinedIcon fontSize="small" />}
                                                        label="Cancelar"
                                                        onClick={() => handleCancel(item.id)}
                                                        rounded={true}
                                                        outlined={true}
                                                        disabled={!canManageExports}
                                                    />
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {history.length == 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Nenhuma exportação encontrada
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ExportHistory;
